// src/pages/Help.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.tsx";
import { TAB_DESCRIPTIONS } from "../constants/tabDescriptions.ts";
import "./Help.css";

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

export const Help: React.FC = () => {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("all");

  const faqItems: FAQItem[] = [
    {
      category: "getting-started",
      question: "How do I create an account?",
      answer: "Click the 'Register' button on the home page. You shall provide a valid email address, username, password, and date of birth. You must be at least 16 years old to register. After registration, you will receive a confirmation email to verify your account."
    },
    {
      category: "getting-started",
      question: "What is Expanded Chess?",
      answer: "Expanded Chess is a sophisticated chess variant system featuring 36 custom pieces across multiple board sizes (8×8 through 12×12). It offers AI opponents, multiplayer functionality, and the ability to create your own custom variants."
    },
    {
      category: "gameplay",
      question: "How do I start a game?",
      answer: "From the Dashboard, click 'Play' to start a local game or play against the AI. For multiplayer games with other players, click 'Multiplayer' to join or host online matches."
    },
    {
      category: "gameplay",
      question: "What board sizes are available?",
      answer: "Expanded Chess supports board sizes from 8×8 (standard chess) up to 12×12. Larger boards allow for more complex games with additional custom pieces."
    },
    {
      category: "gameplay",
      question: "How do the custom pieces move?",
      answer: "Visit the 'Piece Catalog' from the Dashboard to see all 36 custom pieces and their movement patterns. Each piece has unique abilities that add strategic depth to the game."
    },
    {
      category: "multiplayer",
      question: "How do I play with friends?",
      answer: "Go to the 'Multiplayer' section and either create a new game lobby or join an existing one. Share the game code with your friend so they can join your match."
    },
    {
      category: "multiplayer",
      question: "Is there a chat feature?",
      answer: "Yes, registered users can chat with opponents during multiplayer games. Chat is subject to our community guidelines and Terms of Service."
    },
    {
      category: "account",
      question: "How do I reset my password?",
      answer: "Click 'Forgot Password' on the login screen and enter your email address. You will receive an email with instructions to reset your password."
    },
    {
      category: "account",
      question: "How do I delete my account?",
      answer: "Go to Profile page, Click Show Settings for Account Settings and see Cancel Account."
    },
    {
      category: "account",
      question: "Can I change my username?",
      answer: "Currently, usernames cannot be changed after registration. Choose your username carefully during the registration process."
    },
    {
      category: "technical",
      question: "What browsers are supported?",
      answer: "Expanded Chess works best on modern browsers including Chrome, Firefox, Safari, and Edge. We recommend keeping your browser updated to the latest version for the best experience."
    },
    {
      category: "technical",
      question: "The game is running slowly. What can I do?",
      answer: "Try closing other browser tabs, clearing your browser cache, or using a wired internet connection for multiplayer games. Larger board sizes may require more processing power."
    }
  ];

  const categories = [
    { id: "all", label: "All Topics", icon: "📋" },
    { id: "getting-started", label: "Getting Started", icon: "🚀" },
    { id: "gameplay", label: "Gameplay", icon: "🎮" },
    { id: "multiplayer", label: "Multiplayer", icon: "🌐" },
    { id: "account", label: "Account", icon: "👤" },
    { id: "technical", label: "Technical", icon: "🔧" }
  ];

  const filteredFAQs = activeCategory === "all" 
    ? faqItems 
    : faqItems.filter(item => item.category === activeCategory);

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <div className="help-page">
      <PageHeader
        icon={TAB_DESCRIPTIONS.help.icon}
        title={TAB_DESCRIPTIONS.help.label}
        description={TAB_DESCRIPTIONS.help.description}
      />

      <div className="help-content">
        {/* Quick Links Section */}
        <section className="quick-links-section">
          <h2 className="section-title">📚 Quick Links</h2>
          <div className="quick-links-grid">
            <Link to="/terms" className="quick-link-card">
              <span className="quick-link-icon">📜</span>
              <h3>Terms of Service</h3>
              <p>By using Expanded Chess, you agree to our Terms of Service. These terms outline your rights and responsibilities as a user.</p>
            </Link>

            <Link to="/privacy" className="quick-link-card">
              <span className="quick-link-icon">🔒</span>
              <h3>Privacy Policy</h3>
              <p>Learn how we collect, use, store, and protect your personal information in compliance with global privacy regulations.</p>
            </Link>

            <Link to="/contact" className="quick-link-card">
              <span className="quick-link-icon">✉️</span>
              <h3>Contact Us</h3>
              <p>Have questions about our policies or need to exercise your data rights? Contact our team for assistance.</p>
            </Link>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <h2 className="section-title">❓ Frequently Asked Questions</h2>
          
          {/* Category Filter */}
          <div className="faq-categories">
            {categories.map(category => (
              <button
                key={category.id}
                className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-label">{category.label}</span>
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="faq-list">
            {filteredFAQs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${expandedFAQ === index ? 'expanded' : ''}`}
              >
                <button 
                  className="faq-question"
                  onClick={() => toggleFAQ(index)}
                  aria-expanded={expandedFAQ === index}
                >
                  <span className="question-text">{faq.question}</span>
                  <span className="expand-icon">{expandedFAQ === index ? '−' : '+'}</span>
                </button>
                {expandedFAQ === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Still Need Help Section */}
        <section className="still-need-help">
          <div className="help-cta-card">
            <h2>🤔 Still Need Help?</h2>
            <p>Cannot find what you are looking for? Our support team is here to assist you.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="cta-btn primary">
                Contact Support
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Help;
